import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

/**
 * Sidenote — BookQuest 시그니처 컴포넌트 (Tufte 3-column 학파, MUBI tone)
 *
 * 의도:
 *   본문(main) 옆 narrow column에 인용 출처, 책 메타, 인증서 컨텍스트,
 *   페이지 ref 등 mono 11px secondary 보조 정보를 배치.
 *   본문 흐름을 끊지 않고, 학술/저널 같은 격조를 더한다.
 *
 * 레이아웃:
 *   <SidenoteLayout>
 *     <SidenoteLayout.Main>본문 …</SidenoteLayout.Main>
 *     <SidenoteLayout.Aside>
 *       <Sidenote label="인용 출처">『어린왕자』 p.12</Sidenote>
 *       <Sidenote label="발급 인증서" tone="accent">★ Major · 어린왕자</Sidenote>
 *     </SidenoteLayout.Aside>
 *   </SidenoteLayout>
 *
 * 사용처:
 *   - BookDetailPage   책 메타 + 첫 인용 + 관련 인증서
 *   - CredentialsPage  URN + 발급 컨텍스트 + 책 ref
 *   - LoginPage        Why join BookQuest? 3-step
 *   - UploadPage       지원 포맷 + 처리 시간
 *
 * 반응형:
 *   ≥1024px: 3-column (main 1fr + aside 260px)
 *   <1024px: stacked, aside 작은 hairline divider로 구분
 *
 * Tokens-only. 인라인 fontSize/color 금지.
 */

export type SidenoteTone = 'neutral' | 'accent' | 'mute';

export interface SidenoteProps extends HTMLAttributes<HTMLDivElement> {
  /** mono uppercase 라벨 (예: "인용 출처", "발급 인증서"). 없으면 라벨 미표시. */
  label?: string;
  /** 마커 — ref number(1, 2, †, ★) 등. 본문의 ref 마커와 짝지을 때 사용. */
  marker?: ReactNode;
  /** neutral(기본) · accent(terracotta strip) · mute(더 약함) */
  tone?: SidenoteTone;
  children?: ReactNode;
}

export const Sidenote = forwardRef<HTMLDivElement, SidenoteProps>(function Sidenote(
  { label, marker, tone = 'neutral', className, children, ...rest },
  ref,
) {
  const cn = [
    'bq-sidenote',
    `bq-sidenote--${tone}`,
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={ref} className={cn} {...rest}>
      {(label || marker) && (
        <div className="bq-sidenote__head">
          {marker && <span className="bq-sidenote__marker" aria-hidden="true">{marker}</span>}
          {label && <span className="bq-sidenote__label">{label}</span>}
        </div>
      )}
      <div className="bq-sidenote__body">{children}</div>
    </div>
  );
});

/* ─── Layout ───────────────────────────────────────────────────────────── */

export interface SidenoteLayoutProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

const SidenoteLayoutRoot = forwardRef<HTMLDivElement, SidenoteLayoutProps>(
  function SidenoteLayoutRoot({ className, children, ...rest }, ref) {
    return (
      <div ref={ref} className={`bq-sidenote-layout ${className ?? ''}`} {...rest}>
        {children}
      </div>
    );
  },
);

const SidenoteMain = forwardRef<HTMLDivElement, SidenoteLayoutProps>(
  function SidenoteMain({ className, children, ...rest }, ref) {
    return (
      <div ref={ref} className={`bq-sidenote-layout__main ${className ?? ''}`} {...rest}>
        {children}
      </div>
    );
  },
);

const SidenoteAside = forwardRef<HTMLElement, SidenoteLayoutProps>(
  function SidenoteAside({ className, children, ...rest }, ref) {
    return (
      <aside ref={ref} className={`bq-sidenote-layout__aside ${className ?? ''}`} {...rest}>
        {children}
      </aside>
    );
  },
);

type SidenoteLayoutCompound = typeof SidenoteLayoutRoot & {
  Main:  typeof SidenoteMain;
  Aside: typeof SidenoteAside;
};
(SidenoteLayoutRoot as SidenoteLayoutCompound).Main  = SidenoteMain;
(SidenoteLayoutRoot as SidenoteLayoutCompound).Aside = SidenoteAside;

export const SidenoteLayout = SidenoteLayoutRoot as SidenoteLayoutCompound;
export { SidenoteMain, SidenoteAside };
export default Sidenote;
